import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppInputLabel from "@toothfairy/shared-ui/AppInputLabel";
import AppSeparator from "@toothfairy/shared-ui/AppSeparator";
import AppTextInput from "@toothfairy/shared-ui/AppTextInput";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import Color from "../../../../Utility/Color";
import { isLargeScreen } from "../../../MainViewContainer";
import {
  BuilderSection,
  PassthroughSection,
  useBuilderDirtyTracking,
} from "../SubComponents/BuilderSections";

const ALERT_SECTION_FIELD_MAP = {
  overview: ["name", "description", "triggerType", "severity"],
  conditions: ["conditions"],
  recipients: ["recipients", "recipientEmails", "cooldownMinutes"],
  notifications: [
    "isActive",
    "notifyInApp",
    "notifyPush",
    "notifyEmail",
    "notifyWebhook",
  ],
};

const switchProps = (currentTheme) => ({
  wrapperStyle: [
    {
      maxHeight: 30,
      paddingHorizontal: 0,
      paddingVertical: 18,
      margin: 5,
      marginTop: 10,
      marginBottom: 20,
      marginRight: 5,
      width: "auto",
    },
  ],
  labelTextStyle: {
    fontSize: currentTheme?.theme?.small_font_size,
    color: currentTheme?.theme?.darkGrey,
  },
  showTextInputLabel: true,
  displayIsTicked: true,
  descriptionIcon: false,
  descriptionColor: currentTheme?.theme?.mediumGrey,
});

const AlertSettingsView = ({
  isBuilderLayout,
  builderActiveSection,
  onBuilderContextChange,
  saveVersion,
  workspaceUsers,
  triggerTypes,
  severities,
  operators,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const {
    values,
    initialValues,
    setFieldValue,
    errors,
    submitCount,
  } = AppForms.useFormikContext();
  const SectionRenderer = isBuilderLayout ? BuilderSection : PassthroughSection;
  const _showBuilderSection = (sectionId) =>
    builderActiveSection == null || builderActiveSection === sectionId;
  const [lastOpened, setLastOpened] = useState(Date.now());

  useBuilderDirtyTracking({
    isBuilderLayout,
    values,
    initialValues,
    saveVersion,
    onBuilderContextChange,
    errors,
    submitCount,
    sectionFieldMap: ALERT_SECTION_FIELD_MAP,
  });

  useEffect(() => {
    setLastOpened(Date.now());
  }, [values?.recipients]);

  const recipientOptions = useMemo(
    () =>
      (workspaceUsers || []).map((u) => {
        return {
          ...u,
          value: u?.id,
          label: u?.username || u?.user_email,
          category: "user",
          backgroundColor: AppUtilities.stringToHslColor(
            u?.username || u?.user_email,
            71,
          ),
        };
      }),
    [workspaceUsers],
  );

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.border_color,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      let color = new Color(data?.backgroundColor || currentTheme?.theme?.grey);
      return {
        ...styles,
        backgroundColor: isDisabled
          ? undefined
          : isSelected
          ? color
          : isFocused
          ? color.alpha(0.1).css()
          : undefined,
        color: isDisabled
          ? "#ccc"
          : isSelected
          ? currentTheme?.theme?.white
            ? "white"
            : "black"
          : data?.backgroundColor,
        cursor: isDisabled ? "not-allowed" : "default",
        ":active": {
          ...styles[":active"],
          backgroundColor: !isDisabled
            ? isSelected
              ? data?.backgroundColor
              : color.alpha(0.3).css()
            : undefined,
        },
      };
    },
    multiValue: (styles, { data }) => {
      let color = new Color(data?.backgroundColor || currentTheme?.theme?.grey);
      return {
        ...styles,
        backgroundColor: color.alpha(0.1).css(),
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: data?.backgroundColor,
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: data?.backgroundColor,
      ":hover": {
        backgroundColor: data?.backgroundColor,
        color: "white",
      },
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.lightGray,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
    }),
  };

  const updateCondition = (index, key, val) => {
    const conditions = [...(values.conditions || [])];
    conditions[index] = { ...conditions[index], [key]: val };
    setFieldValue("conditions", conditions);
  };

  const addCondition = () => {
    setFieldValue("conditions", [
      ...(values.conditions || []),
      { field: "", op: "eq", value: "" },
    ]);
  };

  const removeCondition = (index) => {
    setFieldValue(
      "conditions",
      (values.conditions || []).filter((_, idx) => idx !== index),
    );
  };

  return (
    <>
      {_showBuilderSection("overview") && (
        <SectionRenderer
          title={t("alerts.builderSectionOverview", {
            defaultValue: "Overview",
          })}
          icon={AppAssets.icons.persona}
        >
          <AppForms.AppFormField
            showTextInputLabel
            isMandatory
            wrapperInputStyle={{ paddingTop: 10 }}
            label={t("alerts.name")}
            placeholder={t("alerts.namePlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="name"
          />
          <AppForms.AppFormField
            showTextInputLabel
            wrapperInputStyle={{ marginBottom: 10, padding: 5 }}
            textContainer={{ padding: 10, marginHorizontal: 0, minHeight: 220 }}
            multiline
            textInputLabelStyle={{
              marginLeft: 5,
            }}
            label={t("alerts.description")}
            placeholder={t("alerts.descriptionPlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="description"
          />
          <View style={{ flexDirection: "row", gap: 10 }}>
            <View style={{ flex: 1 }}>
              <AppForms.AppFormDropDown
                isElevated={false}
                dropDownStyle={{
                  borderColor: currentTheme?.theme?.border_color,
                }}
                showTextInputLabel
                isMandatory
                label={t("alerts.triggerType")}
                name="triggerType"
                items={triggerTypes}
                propertyLabel="label"
                propertyValue="value"
              />
            </View>
            <View style={{ flex: 1 }}>
              <AppForms.AppFormDropDown
                isElevated={false}
                dropDownStyle={{
                  borderColor: currentTheme?.theme?.border_color,
                }}
                showTextInputLabel
                label={t("alerts.severity")}
                name="severity"
                items={severities}
                propertyLabel="label"
                propertyValue="value"
              />
            </View>
          </View>
        </SectionRenderer>
      )}

      {_showBuilderSection("conditions") && (
        <SectionRenderer
          title={t("alerts.builderSectionConditions", {
            defaultValue: "Conditions",
          })}
          icon={AppAssets.icons.filter}
        >
          <AppText
            style={{
              fontSize: currentTheme?.theme?.small_font_size,
              color: currentTheme?.theme?.mediumGrey,
              marginBottom: 10,
              marginLeft: 10,
            }}
          >
            {t("alerts.conditionsDesc")}
          </AppText>
          {(values.conditions || []).map((c, i) => (
            <View
              key={`cond-${i}`}
              style={{
                flexDirection: "row",
                gap: 6,
                marginBottom: 6,
                alignItems: "center",
              }}
            >
              <View style={{ flex: 2 }}>
                <AppTextInput
                  showTextInputLabel={false}
                  value={c.field}
                  onChangeText={(v) => updateCondition(i, "field", v)}
                  placeholder={t("alerts.conditionField")}
                  wrapperInputStyle={{ paddingTop: 0 }}
                />
              </View>
              <View style={{ flex: 1.2 }}>
                <AppDropDown
                  showTextInputLabel={false}
                  isElevated={false}
                  dropDownStyle={{
                    borderColor: currentTheme?.theme?.border_color,
                  }}
                  items={operators}
                  selectedValue={c.op}
                  onValueChange={(v) => updateCondition(i, "op", v)}
                  propertyLabel="label"
                  propertyValue="value"
                />
              </View>
              <View>
                <AppTextInput
                  showTextInputLabel={false}
                  value={String(c.value ?? "")}
                  onChangeText={(v) => updateCondition(i, "value", v)}
                  placeholder={t("alerts.conditionValue")}
                  wrapperInputStyle={{ paddingTop: 0 }}
                />
              </View>
              <AppIcon
                icon={AppAssets?.icons?.close}
                isClickable
                size={16}
                onPress={() => removeCondition(i)}
                wrapperStyle={{ minWidth: 18, height: 40, marginBottom: 2 }}
              />
            </View>
          ))}
          <ToothFairyButton
            title={t("alerts.addCondition")}
            onPress={addCondition}
          />
        </SectionRenderer>
      )}

      {_showBuilderSection("recipients") && (
        <SectionRenderer
          title={t("alerts.builderSectionRecipients", {
            defaultValue: "Recipients",
          })}
          icon={AppAssets.icons.userInfo}
        >
          <AppInputLabel
            label={t("alerts.recipients")}
            style={{ marginTop: 20, marginBottom: 5, marginLeft: 5 }}
          />
          {(workspaceUsers || []).length === 0 ? (
            <AppText
              style={{ color: currentTheme?.theme?.mediumGrey, fontSize: 12 }}
            >
              {t("alerts.noWorkspaceUsers")}
            </AppText>
          ) : (
            <AppForms.AppFormMultiSelect
              key={`recipients-${lastOpened}`}
              name="recipients"
              topErrorOffset={-21}
              touchedByDefault
              placeholder={t("alerts.recipientsPlaceholder")}
              hidePlaceholder={true}
              items={workspaceUsers}
              uniqueKey="id"
              showDropDowns={true}
              readOnlyHeadings={true}
              style={{ maxWidth: 800 }}
              wrapperContainerStyle={{
                flex: !isLargeScreen() ? 1 : 0.5,
                zIndex: 20,
                marginLeft: 5,
                marginTop: 5,
                marginBottom: 33,
              }}
              styles={colourStyles}
              options={recipientOptions}
            />
          )}
          <AppForms.AppFormField
            showTextInputLabel
            label={t("alerts.recipientEmails")}
            placeholder={t("alerts.recipientEmailsPlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="recipientEmails"
          />
          <AppForms.AppFormTrackSlider
            step={1}
            maximumValue={360}
            minimumValue={0}
            label={t("alerts.cooldown")}
            name="cooldownMinutes"
            description={t("alerts.cooldownDesc")}
          />
        </SectionRenderer>
      )}

      {_showBuilderSection("notifications") && (
        <>
          <AppSeparator
            text={""}
            separatorContainer={{
              marginTop: 10,
              marginBottom: 20,
            }}
          />
          <SectionRenderer
            title={t("alerts.notifications")}
            icon={AppAssets.icons.notification}
          >
            <AppText
              style={{
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.mediumGrey,
                fontWeight: "bold",
                marginBottom: 0,
              }}
              wrapperStyle={{
                marginLeft: 10,
                marginBottom: -10,
              }}
            >
              {t("alerts.notificationsDesc")}
            </AppText>
            <AppForms.AppFormSwitch
              {...switchProps(currentTheme)}
              label={t("alerts.active")}
              name="isActive"
              description={t("alerts.activeDesc")}
            />
            <AppForms.AppFormSwitch
              {...switchProps(currentTheme)}
              label={t("alerts.notifyInApp")}
              name="notifyInApp"
              description={t("alerts.notifyInAppDesc")}
            />
            <AppForms.AppFormSwitch
              {...switchProps(currentTheme)}
              label={t("alerts.notifyPush")}
              name="notifyPush"
              description={t("alerts.notifyPushDesc")}
            />
            <AppForms.AppFormSwitch
              {...switchProps(currentTheme)}
              label={t("alerts.notifyEmail")}
              name="notifyEmail"
              description={t("alerts.notifyEmailDesc")}
            />
            <AppForms.AppFormSwitch
              {...switchProps(currentTheme)}
              label={t("alerts.notifyWebhook")}
              name="notifyWebhook"
              description={t("alerts.notifyWebhookDesc")}
            />
          </SectionRenderer>
        </>
      )}
    </>
  );
};

export default AlertSettingsView;
